/**
 * useEasyApply — the one-page Easy Apply form (/easyApplyApplication/:id).
 * Mirrors Flutter CandidateFormBloc with isEasyApply = true
 * (lib/application/candidate_form/candidate_form_bloc.dart):
 *   _CandidateAplicationFormInit → getEasyApplyFormContent + _GetJobDetailsById → _Init
 *   → _GetPartialApplnFormData (draft) / _GetMasterDetails → _PopulateEasyApplyData
 *   Apply → formKey.validate() → _OnClickEasyApply (validateEasyApplyForm + setEasyApplyUpdatedValues)
 *
 * The form is Resume + the Basic Information fields (easyApplyApplicationForm → personalInformation).
 * Email and mobile always come from the candidate account and are read-only.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/app/router/routes';
import { useTenantStore } from '@/store/tenantStore';
import { useAppStateStore } from '@/store/appStateStore';
import { useLocalizationStore } from '@/store/localizationStore';
import { getUserDetails, getEasyApplyFormContent } from '@/services/api/authApi';
import {
  getMasterData,
  getPartialApplicationFormData,
  uploadResumeForApplication,
  saveApplication,
  downloadResume,
  fetchMasterDataFromResume,
} from '@/services/api/applicationApi';
import { getJobDetails } from '@/services/api/homeApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import { validateResumeFile } from '@/features/jobs/utils/resumeFile';
import {
  getApplicationPageFields,
  getJobReferralData,
  setDefaultApplicationOrigin,
} from '@/features/jobs/utils/jobOrigin';
import {
  parseApplicationFormJson,
  getMonthsToYearArray,
  calculateExperienceInMonths,
  validateFormField,
  applyGenderMandatoryInCountries,
} from '../utils/formHelpers';
import type { ParsedApplication } from '../utils/formHelpers';
import type { ProfileInput, FldListState } from '@/types/application.types';
import { ApplicationStatus, ClickOptions, FieldType } from '@/types/application.types';
import { FILL_ALL_MANDATORY, GENERIC_ERROR, uploadFailureMessage } from './useApplicationForm';

/* ── Flutter string constants (English; Toast translates them) ── */
const RESUME_REQUIRED = 'Please upload your resume before applying';
const REQUIRED_FIELDS_MISSING = 'Please fill all required fields';
const APPLICATION_SUBMITTED = 'Application submitted successfully!';

/** Flutter _PopulateEasyApplyData — the only form fields filled from a draft / master data / resume. */
const POPULATED_KEYS = ['firstName', 'lastName', 'currentLocation', 'location'];
/** Flutter validateEasyApplyForm — fields that must have a value when mandatory. */
const REQUIRED_KEYS = ['firstName', 'lastName', 'primaryEmailId', 'primaryContactNumber', 'currentLocation', 'location'];

interface Toast {
  message: string;
  type: 'success' | 'error';
}

interface CandidateContact {
  primaryEmailId: string;
  primaryContactNumber: string;
  primaryContactNumberCountryCode: string;
}

export interface EasyApplyState {
  isInitializing: boolean;
  isSubmitting: boolean;
  isUploading: boolean;
  application: ParsedApplication | null;
  profileInput: ProfileInput;
  candidateId: string;
  /** Flutter candidateDetails — email and mobile are sent from the account. */
  candidate: CandidateContact;
  /** Flutter s3url — the stored resume URL, or the name of the file just uploaded. */
  resumeUrl: string;
  resumeUploaded: boolean;
  uploadProgress: number;
  isLoadingResumeData: boolean;
  appliedStatus: string;
  showContinuePopup: boolean;
  /** Validation errors by Basic Information field index. */
  errors: Record<number, string>;
  toast: Toast | null;
}

/* ────────────────────────────── Pure helpers ────────────────────────────── */

function contactOf(candidate: Record<string, unknown>): CandidateContact {
  return {
    primaryEmailId: String(candidate.primaryEmailId ?? ''),
    primaryContactNumber: String(candidate.primaryContactNumber ?? ''),
    primaryContactNumberCountryCode: String(candidate.primaryContactNumberCountryCode ?? ''),
  };
}

/** Email and mobile are the account's, shown read-only ("+91 98765 43210"). */
function withContactFields(fields: FldListState[], contact: CandidateContact): FldListState[] {
  return fields.map((f) => {
    if (f.key === 'primaryContactNumber') {
      const number = contact.primaryContactNumber;
      const display = number ? `+${contact.primaryContactNumberCountryCode} ${number}`.trim() : '';
      return { ...f, value: display, displayValue: display, isDisabled: true };
    }
    if (f.key === 'primaryEmailId') {
      return { ...f, value: contact.primaryEmailId, displayValue: contact.primaryEmailId, isDisabled: true };
    }
    return f;
  });
}

const str = (v: unknown) => (v == null ? '' : String(v));

/**
 * Flutter _PopulateEasyApplyData — first / last name and current location from personalInformation,
 * total experience split into the years / months pair. Returns null when there is nothing to populate.
 */
function populateEasyApply(
  fields: FldListState[],
  raw: unknown,
): { fields: FldListState[]; resumeUrl: string } | null {
  if (!raw || typeof raw !== 'object' || Object.keys(raw as object).length === 0) return null;
  const pi = (raw as Record<string, unknown>).personalInformation as Record<string, unknown> | undefined;
  const resumeUrl = str(pi?.candidateUrl);
  if (!pi || Object.keys(pi).length === 0) return { fields, resumeUrl };

  let next = fields.map((f) => {
    if (!POPULATED_KEYS.includes(f.key)) return f;
    if (f.type === FieldType.TEXT_FLD) {
      return pi[f.key] != null && pi[f.key] !== '' ? { ...f, value: str(pi[f.key]) } : f;
    }
    // Dropdowns: the option key is stored as key<Name>, the label under the plain key
    const keyName = `key${f.key[0].toUpperCase()}${f.key.slice(1)}`;
    if (pi[keyName] != null) {
      return { ...f, value: str(pi[keyName]), displayValue: str(pi[f.key]) };
    }
    if (f.key === 'currentLocation' || f.key === 'location') {
      const address = Array.isArray(pi.address) ? (pi.address as Record<string, unknown>[]) : [];
      const location =
        address.length > 0
          ? str(address[0]?.currentLocation)
          : str(pi.currentLocation ?? pi.location ?? pi[f.key]);
      return { ...f, value: location, displayValue: location };
    }
    return { ...f, value: str(pi[f.key]), displayValue: str(pi[f.key]) };
  });

  if (pi.experienceInMonths != null && pi.experienceInMonths !== '') {
    const [years, months] = getMonthsToYearArray(Number(pi.experienceInMonths));
    next = next.map((f) =>
      f.key === 'experienceInYears' ? { ...f, value: years } : f.key === 'experienceInMonths' ? { ...f, value: months } : f,
    );
  }
  return { fields: next, resumeUrl };
}

/**
 * Flutter validateEasyApplyForm — resume, the key contact fields and (when mandatory) years of experience.
 * D2i: Flutter also fails when the form has no mandatory experienceInYears field, so Easy Apply
 * could never submit; here a missing or optional years field passes.
 */
function isEasyApplyFormComplete(fields: FldListState[], resumeUrl: string): boolean {
  if (!resumeUrl) return false;
  for (const f of fields) {
    if (REQUIRED_KEYS.includes(f.key) && f.isMandatory && !(f.value ?? '').trim()) return false;
  }
  const years = fields.find((f) => f.key === 'experienceInYears');
  return !(years?.isMandatory && !(years.value ?? '').trim());
}

/** Flutter setEasyApplyUpdatedValues + the EASY_APPLIED status set by _OnClickEasyApply. */
function buildEasyApplyPayload(s: EasyApplyState): Record<string, unknown> {
  const personal: Record<string, unknown> = {
    candidateUrl: s.resumeUrl,
    primaryEmailId: s.candidate.primaryEmailId,
    primaryContactNumber: s.candidate.primaryContactNumber,
    primaryContactNumberCountryCode: s.candidate.primaryContactNumberCountryCode,
  };
  const fields = s.application?.personalInformation.fields ?? [];
  for (const f of fields) {
    if (!f.key || f.key === 'primaryContactNumber' || f.key === 'primaryEmailId' || f.key === 'experienceInMonths') {
      continue;
    }
    // Either location field is saved as the candidate's current location
    if (f.key === 'currentLocation' || f.key === 'location') personal.currentLocation = f.value ?? '';
    personal[f.key] = f.value ?? '';
  }

  // Total experience in months from the years / months pair
  const yearsIdx = fields.findIndex((f) => f.key === 'experienceInYears' && f.subType === 'EXP');
  if (yearsIdx >= 0) {
    const months = fields.slice(yearsIdx + 1).find((f) => f.key === 'experienceInMonths');
    personal.experienceInMonths = String(
      calculateExperienceInMonths(fields[yearsIdx].value || '0', months?.value || '0'),
    );
  }

  return {
    jobsInfo: {
      id: s.profileInput.jobId ?? '',
      displayId: s.profileInput.jobDisplayId,
      jobTitle: s.profileInput.jobTitle,
    },
    appliedStatus: ApplicationStatus.EASY_APPLIED,
    personalInformation: personal,
  };
}

function cachedEasyApplyForm(): unknown {
  try {
    return JSON.parse(localStorage.getItem('easyApplyFormContent') ?? 'null');
  } catch {
    return null;
  }
}

/* ────────────────────────────── Hook ────────────────────────────── */

export function useEasyApply() {
  // Flutter routing_config: /application and /easyApplyApplication call setDefaultApplicationOrigin()
  useEffect(() => {
    setDefaultApplicationOrigin();
  }, []);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id: jobDisplayId } = useParams<{ id: string }>();
  const tenantConfig = useTenantStore((s) => s.config);
  const currentLanguage = useLocalizationStore((s) => s.currentLanguage);

  const [state, setState] = useState<EasyApplyState>({
    isInitializing: true,
    isSubmitting: false,
    isUploading: false,
    application: null,
    profileInput: { jobDisplayId: jobDisplayId ?? '' },
    candidateId: '',
    candidate: { primaryEmailId: '', primaryContactNumber: '', primaryContactNumberCountryCode: '' },
    resumeUrl: '',
    resumeUploaded: false,
    uploadProgress: 0,
    isLoadingResumeData: true,
    appliedStatus: '',
    showContinuePopup: false,
    errors: {},
    toast: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;
  const busyRef = useRef(false);

  const patch = useCallback((partial: Partial<EasyApplyState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  /** Flutter CustomFlushBar — `message` is Flutter's English text; Toast translates it. */
  const showToast = useCallback(
    (message: string, type: Toast['type'] = 'error') => patch({ toast: { message, type } }),
    [patch],
  );

  /** Apply a populate result (draft, master data or resume auto-fill). */
  const applyPopulate = useCallback((raw: unknown) => {
    setState((prev) => {
      if (!prev.application) return { ...prev, isLoadingResumeData: false };
      const result = populateEasyApply(prev.application.personalInformation.fields, raw);
      if (!result) return { ...prev, isLoadingResumeData: false };
      const personalInformation = {
        ...prev.application.personalInformation,
        fields: withContactFields(result.fields, prev.candidate),
      };
      return {
        ...prev,
        isLoadingResumeData: false,
        application: { ...prev.application, personalInformation },
        ...(result.resumeUrl ? { resumeUrl: result.resumeUrl, resumeUploaded: true } : {}),
      };
    });
  }, []);

  /* ── Language change reloads the page (Flutter languageStream → window.location.reload) ── */
  const languageRef = useRef(currentLanguage);
  useEffect(() => {
    if (languageRef.current !== currentLanguage) window.location.reload();
  }, [currentLanguage]);

  /*
   * ── Init ──
   * Signed-out users never get here: ProtectedRoute sends them to /login and back.
   */
  useEffect(() => {
    // Flutter only loads the job when there is an id; without one the empty form is shown
    if (!jobDisplayId) {
      patch({ isInitializing: false });
      return;
    }
    let cancelled = false;

    (async () => {
      const [formJson, jobData, candidateData] = await Promise.all([
        getEasyApplyFormContent().catch(cachedEasyApplyForm),
        getJobDetails(jobDisplayId).catch(() => null),
        (async () => useAppStateStore.getState().candidateDetails ?? (await getUserDetails().catch(() => null)))(),
      ]);
      if (cancelled) return;

      if (!jobData) {
        showToast(GENERIC_ERROR);
        setTimeout(() => {
          void analyticsService.logScreenView({ screenName: ROUTES.HOME });
          navigate(ROUTES.HOME, { replace: true });
        }, 1000);
        return;
      }

      const job = jobData as Record<string, unknown>;
      const locations = (job.jobLocation as Array<{ fqLocationName?: string; country?: string }> | undefined) ?? [];
      const skills = ((job.skills as Array<{ skill?: string }> | undefined) ?? [])
        .map((s) => s.skill)
        .filter(Boolean)
        .join(', ');
      const profileInput: ProfileInput = {
        clickedOn: ClickOptions.APPLIED,
        profileTabIndex: -1,
        tabIndex: 4,
        skills,
        jobDisplayId: (job.displayId as string) ?? jobDisplayId,
        jobTitle: (job.jobTitle as string) ?? '',
        jobLocation: locations[0]?.fqLocationName ?? '',
        jobId: (job.id as string) ?? '',
      };

      const candidate = (candidateData ?? {}) as Record<string, unknown>;
      const contact = contactOf(candidate);
      const parsed = parseApplicationFormJson(formJson as never);
      if (parsed) {
        parsed.education = [];
        parsed.experience = [];
        // Flutter _Init runs this for Easy Apply too
        applyGenderMandatoryInCountries(parsed, locations[0]?.country ?? '', useTenantStore.getState().preferences);
        parsed.personalInformation = {
          ...parsed.personalInformation,
          fields: withContactFields(parsed.personalInformation.fields, contact),
        };
      }

      setState((prev) => ({
        ...prev,
        isInitializing: false,
        application: parsed,
        profileInput,
        candidateId: (candidate.id as string) ?? '',
        candidate: contact,
      }));

      // Draft for this job, otherwise master data
      let draft: unknown = null;
      try {
        draft = await getPartialApplicationFormData(jobDisplayId);
      } catch {
        draft = null;
      }
      if (cancelled) return;
      const draftData = draft && typeof draft === 'object' ? (draft as Record<string, unknown>) : null;
      // An empty draft (`200 {}`) also falls back to master data; Flutter would populate nothing
      if (draftData && Object.keys(draftData).length > 0) {
        const status = (draftData.appliedStatus as string) ?? '';
        setState((prev) => ({
          ...prev,
          profileInput: { ...prev.profileInput, applicationId: (draftData.applicationId as string) ?? '' },
          appliedStatus: status,
          showContinuePopup: status === ApplicationStatus.APPLIED,
        }));
        applyPopulate(draftData);
        return;
      }
      const master = await getMasterData().catch(() => null);
      if (!cancelled) applyPopulate(master);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDisplayId]);

  /* ── Field updates ── */

  const updateField = useCallback((index: number, value: string, displayValue?: string) => {
    setState((prev) => {
      if (!prev.application) return prev;
      const fields = prev.application.personalInformation.fields.map((f, i) =>
        i === index ? { ...f, value, displayValue: displayValue ?? (value ? f.displayValue : '') } : f,
      );
      const errors = { ...prev.errors };
      delete errors[index];
      return {
        ...prev,
        errors,
        application: { ...prev.application, personalInformation: { ...prev.application.personalInformation, fields } },
      };
    });
  }, []);

  /* ── Resume ── */

  /** Flutter _SelectFilesFromSystem → _UploadResume (applyType EASY_APPLY) + auto-fill from the resume. */
  const selectResume = useCallback(
    async (file: File) => {
      const fileError = validateResumeFile(file);
      if (fileError) {
        showToast(fileError);
        return;
      }
      const s = stateRef.current;
      patch({ isUploading: true, uploadProgress: 0 });

      if ((tenantConfig as Record<string, unknown> | null)?.enableAutoFillByUploadResume) {
        fetchMasterDataFromResume(file).then(applyPopulate).catch(() => {});
      }

      const referral = getJobReferralData();
      try {
        // Easy apply keeps its applicationId — the upload response key is not used
        await uploadResumeForApplication(
          file,
          s.candidateId,
          s.profileInput.jobId ?? '',
          { channel: referral.channel ?? '', sourceName: referral.sourceName ?? '' },
          { ...getApplicationPageFields(), applyType: 'EASY_APPLY' },
          (pct) => patch({ uploadProgress: pct }),
        );
        patch({ isUploading: false, resumeUrl: file.name, resumeUploaded: true, uploadProgress: 100 });
        void analyticsService.logEvent('Apply Job Form', 'uploaded_resume');
      } catch (err) {
        // Flutter: resumeUploadStatus false + the repository's message
        patch({ isUploading: false, uploadProgress: 0, resumeUrl: '', resumeUploaded: false });
        showToast(uploadFailureMessage(err));
      }
    },
    [applyPopulate, patch, showToast, tenantConfig],
  );

  const downloadCurrentResume = useCallback(async () => {
    try {
      const { data, fileName } = await downloadResume();
      const url = URL.createObjectURL(data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName.replace(/"$/, '');
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      // Flutter _DownloadResume (shared with the full form)
      void analyticsService.logEvent('Apply Job Form', 'download_resume');
    } catch {
      // Flutter _DownloadResume ignores a failed download
    }
  }, []);

  const deleteResume = useCallback(() => {
    patch({ resumeUrl: '', resumeUploaded: false, uploadProgress: 0 });
  }, [patch]);

  /* ── Apply ── */

  /** Flutter WebEasyApplyBasicInformation "Apply" → formKey.validate() → _OnClickEasyApply. */
  const apply = useCallback(async () => {
    const s = stateRef.current;
    const fields = s.application?.personalInformation.fields;
    if (!fields || busyRef.current) return;

    const errors: Record<number, string> = {};
    fields.forEach((field, index) => {
      if (field.isDisabled) return;
      const err = validateFormField(field, t);
      if (err) errors[index] = err;
    });
    if (Object.keys(errors).length > 0) {
      patch({ errors });
      showToast(FILL_ALL_MANDATORY);
      return;
    }
    void analyticsService.logEvent('Easy Apply - Basic Information', 'click_apply');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!s.resumeUrl) {
      showToast(RESUME_REQUIRED);
      return;
    }
    if (!isEasyApplyFormComplete(fields, s.resumeUrl)) {
      showToast(REQUIRED_FIELDS_MISSING);
      return;
    }

    const referral = getJobReferralData();
    const payload: Record<string, unknown> = {
      ...buildEasyApplyPayload(s),
      ...getApplicationPageFields(),
      ...(referral.channel || referral.sourceName ? { channel: referral.channel, sourceName: referral.sourceName } : {}),
    };

    busyRef.current = true;
    patch({ isSubmitting: true, errors: {} });
    try {
      const applicationId = await saveApplication(
        s.candidateId,
        s.profileInput.jobId ?? '',
        s.profileInput.applicationId,
        payload,
      );
      setState((prev) => ({
        ...prev,
        profileInput: { ...prev.profileInput, applicationId: applicationId || prev.profileInput.applicationId },
        toast: { message: APPLICATION_SUBMITTED, type: 'success' },
      }));
      void analyticsService.logEvent('Easy Apply', 'application_submitted');
      // Flutter waits 1.5s on the success message, then navigateTo(CoreRoute.profile) with
      // profileTabIndex 1, i.e. My Jobs → Applied (screen_view is logged for '/profile')
      setTimeout(() => {
        void analyticsService.logScreenView({ screenName: ROUTES.PROFILE });
        navigate(ROUTES.VIEW_JOBS_APPLIED, { replace: true });
      }, 1500);
    } catch {
      // Flutter shows `l ?? 'Application failed'`, and saveApplication's `l` is always genericError
      busyRef.current = false;
      patch({ isSubmitting: false });
      showToast(GENERIC_ERROR);
    }
  }, [navigate, patch, showToast, t]);

  /** Flutter confirmEditAppliedJob dialog — cancelling goes back home. */
  const dismissContinuePopup = useCallback(
    (proceed: boolean) => {
      patch({ showContinuePopup: false });
      if (!proceed) {
        void analyticsService.logScreenView({ screenName: ROUTES.HOME });
        navigate(ROUTES.HOME, { replace: true });
      }
    },
    [navigate, patch],
  );

  const clearToast = useCallback(() => patch({ toast: null }), [patch]);

  return {
    state,
    actions: {
      updateField,
      selectResume,
      downloadResume: downloadCurrentResume,
      deleteResume,
      apply,
      dismissContinuePopup,
      clearToast,
    },
  };
}
